import React, { Fragment, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';

import useStateRef from './useStateRef';

const labelMatch = (currentInput, item) =>
  item.label.substr(0, currentInput.length).toLowerCase() === currentInput.toLowerCase();

const indexOfMatch = (currentInput, item) =>
  item.label.toLowerCase().indexOf(currentInput.toLowerCase());

const indexOfItem = (item, items) => items.indexOf(items.find((i) => i.key === item.key));

export const DataListInput = ({
  className,
  dataTestId,
  activeItemClassName,
  dropdownClassName,
  dropDownLength,
  value,
  inputClassName,
  itemClassName,
  onSelect,
  onBlur,
  placeholder,
  requiredInputLength = 1,
  items,
  field,
}) => {
  const [lastValidItem, setLastValidItem] = useState();
  const [currentInput, setCurrentInput, currentInputRef] = useStateRef(
    value !== undefined ? value : '',
  );
  const [matchingItems, setMatchingItems] = useState([]);
  const [visible, setVisible, visibleRef] = useStateRef(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const interactionHappenedRef = useRef(false);
  const [isMatchingDebounced, setIsMatchingDebounced] = useState(false);

  const menu = useRef<HTMLDivElement | null>(null);
  const inputField = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const onClickCloseMenu = (event) => {
      if (!menu.current) {
        return;
      }
      // if rerender, items inside might change, allow one click without further checking
      if (interactionHappenedRef.current) {
        interactionHappenedRef.current = false;
        return;
      }
      // do not do anything if input is clicked, as we have a dedicated func for that
      if (!inputField.current) {
        return;
      }
      const targetIsInput = event.target === inputField.current;
      const targetInInput = inputField.current.contains(event.target);
      if (targetIsInput || targetInInput) {
        return;
      }

      // do not close menu if user clicked inside
      const targetInMenu = menu.current.contains(event.target);
      const targetIsMenu = event.target === menu.current;
      if (targetInMenu || targetIsMenu) {
        return;
      }

      if (visibleRef.current) {
        setVisible(false);
        setFocusIndex(-1);
      }
    };
    window.addEventListener('click', onClickCloseMenu, false);
    return () => {
      window.removeEventListener('click', onClickCloseMenu);
    };
  }, [setVisible, visibleRef]);

  useEffect(() => {
    const isValuePropSet = value !== undefined;
    const isValueDifferent = currentInputRef.current !== value;
    const isMatchingRunning = visible || isMatchingDebounced;
    if (isValuePropSet && isValueDifferent && !isMatchingRunning) {
      setCurrentInput(value);
    }
  }, [visible, isMatchingDebounced, value, setCurrentInput, currentInputRef]);

  const debouncedMatchingUpdateStep = useCallback(
    (nextInput) => {
      const reachedRequiredLength = nextInput.length >= requiredInputLength;
      const showMatchingStillLoading = false;
      setCurrentInput(nextInput);
      setIsMatchingDebounced(showMatchingStillLoading);
      // no matching if we do not reach required input length
      if (!reachedRequiredLength) {
        return;
      }

      const updateMatchingItems = () => {
        // matching process to find matching entries in items array
        const updatedMatchingItems = items.filter((item) => {
          return labelMatch(nextInput, item);
        });
        const displayableItems = updatedMatchingItems.slice(0, dropDownLength);
        const showDragIndex = !!lastValidItem;
        const index = showDragIndex ? indexOfItem(lastValidItem, displayableItems) : 0;
        if (displayableItems.length) {
          setMatchingItems(displayableItems);
          setFocusIndex(index > 0 ? index : 0);
          setIsMatchingDebounced(false);
          setVisible(true);
        } else {
          if (visibleRef.current) {
            setVisible(false);
          }
          setMatchingItems(displayableItems);
          setFocusIndex(-1);
          setIsMatchingDebounced(false);
        }
      };

      updateMatchingItems();
    },
    [
      requiredInputLength,
      setCurrentInput,
      items,
      dropDownLength,
      lastValidItem,
      setVisible,
      visibleRef,
    ],
  );

  const onHandleInput = useCallback(
    (event) => {
      const { value: newValue } = event.target;
      debouncedMatchingUpdateStep(newValue);
    },
    [debouncedMatchingUpdateStep],
  );

  const onClickInput = useCallback(() => {
    const currentValue = currentInputRef.current;
    const reachedRequiredLength = currentValue.length >= requiredInputLength;
    if (reachedRequiredLength && !visibleRef.current) {
      debouncedMatchingUpdateStep(currentValue);
    }
  }, [currentInputRef, requiredInputLength, visibleRef, debouncedMatchingUpdateStep]);

  const onHandleBlur = useCallback(
    (event) => {
      const { value: newValue } = event.target;
      onBlur(newValue);
    },
    [onBlur],
  );

  const onHandleSelect = useCallback(
    (selectedItem) => {
      if (isMatchingDebounced) {
        return;
      }

      setCurrentInput(selectedItem.label);
      setVisible(false);
      setFocusIndex(-1);
      interactionHappenedRef.current = true;
      setLastValidItem(selectedItem?.key || selectedItem);
      onSelect(selectedItem?.key || selectedItem);
    },
    [isMatchingDebounced, setCurrentInput, setVisible, lastValidItem, onSelect],
  );

  const onHandleKeydown = useCallback(
    (event) => {
      if (!visibleRef.current) {
        return;
      }
      let currentFocusIndex = focusIndex;
      if (event.keyCode === 40 || event.keyCode === 9) {
        currentFocusIndex += 1;
        if (currentFocusIndex >= matchingItems.length) {
          currentFocusIndex = 0;
        }
        setFocusIndex(currentFocusIndex);
        event.preventDefault();
      } else if (event.keyCode === 38) {
        currentFocusIndex -= 1;
        if (currentFocusIndex <= -1) {
          currentFocusIndex = matchingItems.length - 1;
        }
        setFocusIndex(currentFocusIndex);
      } else if (event.keyCode === 13) {
        if (focusIndex > -1) {
          const selectedItem = matchingItems[currentFocusIndex];
          onHandleSelect(selectedItem);
        }
      }
    },
    [focusIndex, matchingItems, onHandleSelect, visibleRef],
  );

  const renderItemLabel = useCallback(
    (item) => {
      const index = indexOfMatch(currentInput, item);
      const inputLength = currentInput.length;
      return index >= 0 && inputLength ? (
        // renders label with matching search string marked
        <Fragment>
          {item.label.substr(0, index)}
          <strong>{item.label.substr(index, inputLength)}</strong>
          {item.label.substr(index + inputLength, item.label.length)}
        </Fragment>
      ) : (
        item.label
      );
    },
    [currentInput],
  );

  const renderItems = useCallback(
    () => (
      <Fragment>
        {matchingItems.map((item, i) => {
          const isActive = focusIndex === i;
          const itemActiveClasses = isActive
            ? `cursor-pointer ${
                activeItemClassName || 'bg-saffron text-white border-solid border-grey-light p-10;'
              }`
            : '';
          const itemClasses = `${itemClassName} ${itemActiveClasses}`;
          return (
            <div
              onClick={() => onHandleSelect(item)}
              className={itemClasses}
              key={item.key}
              tabIndex={0}
              role="button"
              aria-label={item.label}
              onKeyUp={(event) => event.preventDefault()}
            >
              {renderItemLabel(item)}
            </div>
          );
        })}
      </Fragment>
    ),
    [
      matchingItems,
      focusIndex,
      activeItemClassName,
      itemClassName,
      onHandleSelect,
      renderItemLabel,
    ],
  );

  const dropDown = useMemo(() => {
    const reachedRequiredLength = currentInputRef.current.length >= requiredInputLength;
    if (reachedRequiredLength && isMatchingDebounced) {
      return (
        <div
          ref={menu}
          className={`absolute inset-x-0 top-full z-1 ${
            dropdownClassName || 'grey-light cursor-pointer border border-solid bg-white p-10'
          }`}
          role="dialog"
          aria-label="Dropdown menu"
        >
          <div className={itemClassName}>loading...</div>
        </div>
      );
    }

    if (reachedRequiredLength && visible) {
      return (
        <div
          ref={menu}
          className={`absolute inset-x-0 top-full z-1 ${
            dropdownClassName || 'grey-light cursor-pointer border border-solid bg-white p-10'
          }`}
          role="dialog"
          aria-label="Dropdown menu"
        >
          {renderItems()}
        </div>
      );
    }
    return undefined;
  }, [
    currentInputRef,
    requiredInputLength,
    isMatchingDebounced,
    visible,
    dropdownClassName,
    itemClassName,
    renderItems,
  ]);

  return (
    <div className={classnames('relative isolate inline-block w-full', className)}>
      <input
        {...field}
        aria-label="Search"
        autoComplete="off"
        className={classnames(inputClassName, 'w-full')}
        data-testid={dataTestId}
        id={field.name}
        onBlur={onHandleBlur}
        onChange={onHandleInput}
        onClick={onClickInput}
        onKeyDown={onHandleKeydown}
        placeholder={placeholder}
        ref={inputField}
        type="text"
        value={currentInput}
      />
      {dropDown}
    </div>
  );
};
